import { Component, OnInit } from '@angular/core';
import { trigger, transition } from '@angular/animations';
import { BackdropService } from 'src/app/services/backdrop/backdrop.service';
import { Country, RegisterService, SetUpFormErrors } from 'src/app/services/register/register.service';
import { AuthService } from 'src/app/services/auth/auth.service';
import { EditModeService } from 'src/app/services/edit-mode/edit-mode.service';
import { QuestionnaireService } from 'src/app/services/questionnaire/questionnaire.service';
import { BuilderService } from 'src/app/services/builder/builder.service';
import { Router } from '@angular/router';
import { FormBuilder, Validators } from "@angular/forms";
import { hasOneUppercaseValidator } from "./has-one-uppercase-validator";
import { hasNoEmptySpacesValidator } from "./has-no-empty-spaces-validator";
import { faCheck, faCircleNotch, faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import { I18nHelperService, TranslateService } from '@xref/ngx';
import { combineLatest, forkJoin, Subject } from "rxjs";
import constants from 'src/app/shared/constants';
import { environment } from 'src/environments/environment';
import { TokenManagementService } from "../../services/token-management/token-management.service";
import { find, forEach } from 'lodash';
import { JwtHelperService } from '@auth0/angular-jwt';

import * as Cookies from 'js-cookie';

@Component({
  selector: 'xqnr-register-dialog',
  templateUrl: './register-dialog.component.pug',
  styleUrls: ['./register-dialog.component.scss'],
  animations: [
    trigger('showHide', [
      transition(':enter', []),
      transition(':leave', [])
    ])
  ],
  standalone: false
})
export class RegisterDialogComponent implements OnInit {
  /* Based on:
  * - login.xref.com:RegDetailsComponent
  * - login.xref.com:RegSetUpComponent
  * - EditDialogComponent
  *
  * Notes:
  * - countries dropdown requires both translations and translations api to be ready
  * - phoneCountry is disabled, won't be included in from.value, use getRawValue()
  * */
  // show spinner after form is submitted and until it finishes
  emailReqLoading = false;
  questionnaireSending = false;
  showGenericError = false;
  mostUsedCountries: Array<Country> = [];
  countryList: Array<Country> = [];
  passwordIsVisible: false;
  countryListAvailable$ = new Subject();
  registerForm = this.fb.group({
    country: ['', [Validators.required]],
    phoneCountry: [{ value: null, disabled: true }, Validators.required],
    phoneNumber: ['', Validators.required],
    organisation: ['', {
      validators: [
        hasNoEmptySpacesValidator,
        Validators.required
      ],
      updateOn: 'change'
    }],
    password: ['', {
      validators: [
        Validators.required,
        Validators.minLength(8),
        hasOneUppercaseValidator
      ],
      updateOn: 'change'
    }],
    tc: [false, [Validators.required]]
  });
  fa = {
    circleNotch: faCircleNotch,
    eye: faEye,
    check: faCheck,
    eyeSlash: faEyeSlash,
  };
  LOGIN_URL = environment.LOGIN_URL;
  // used in resending verification when pending, only
  email = this.token.decodeAuthToken().email;

  constructor(
    private backdrop: BackdropService,
    private auth: AuthService,
    private editMode: EditModeService,
    private qs: QuestionnaireService,
    private builder: BuilderService,
    private router: Router,
    private fb: FormBuilder,
    public register: RegisterService,
    private translate: TranslateService,
    private i18nHelper: I18nHelperService,
    private token: TokenManagementService,
    private jwtHelper: JwtHelperService
  ) {
    combineLatest([
      this.countryListAvailable$,
      this.translate.onLangChange
    ]).subscribe((data) => {
      this.translateCountries();
    });
    this.setCountryList();
  }

  get country() {
    return this.registerForm.controls.country;
  }

  ngOnInit() {
    this.backdrop.active$.subscribe((activeState: boolean) => {
      if (!activeState) {
        this.register.hide();
        this.register.setUpFormErrors.ERROR_PENDING_VERIFICATION = false;
      }
    });
  }

  showRegisterDialog() {
    this.register.show();
    this.backdrop.show();
  }

  hideRegisterDialog() {
    this.register.hide();
    this.backdrop.hide();
    this.register.setUpFormErrors.ERROR_PENDING_VERIFICATION = false;
  }

  setCountryList() {
    this.register.getCountryDetails().subscribe(
      (res: any) => {
        forEach(res, e => e.translationKey = e.country);
        this.countryList = res;
        this.countryListAvailable$.next(null);
        this.getMostUsedCountries(['Australia', 'Canada', 'United Kingdom', 'United States']);
        this.setCountryByIP();
      }
    );
  }

  setCountryByIP() {
    try {
      window['geoip2'].country((res) => {
        const resolvedCountryName = res.country.names.en;
        const countryInstance = find(this.countryList, { translationKey: resolvedCountryName });

        // once IP is resolved, update country and phone unless already set
        if (!this.registerForm.controls.country.value) {
          this.registerForm.controls.country.setValue(countryInstance.id);
          this.registerForm.controls.phoneCountry.setValue(countryInstance.phone_code);
        }
      });
    } catch (error) {
      console.warn('geo ip library could not be resolved.', error);
    }

  }

  countryChanged() {
    const selectedCountry = parseInt(this.registerForm.controls.country.value, 10);
    const phoneCode = find(this.countryList, { id: selectedCountry }).phone_code;
    this.registerForm.controls.phoneCountry.setValue(phoneCode);
  }

  hasOneUppercase() {
    const password = this.registerForm.controls.password;
    return !(!!password.errors && !!password.errors.hasOneUppercase);
  }

  longEnough() {
    const password = this.registerForm.controls.password;
    return !(
      !!password.errors &&
      (!!password.errors.minlength || !!password.errors.required)
    );
  }

  showPassSpecialCharErr() {
    const password = this.registerForm.controls.password;
    return !(!!password.errors && !!password.errors.hasSpecialChar);
  }

  showPassLowercaseErr() {
    const password = this.registerForm.controls.password;
    return !(!!password.errors && !!password.errors.hasLowercase);
  }

  showPassNumberErr() {
    const password = this.registerForm.controls.password;
    return !(!!password.errors && !!password.errors.hasNumber);
  }

  resetSetUpFormErrors() {
    this.register.setUpFormErrors = new SetUpFormErrors();
  }

  submit() {
    this.emailReqLoading = true;
    this.showGenericError = false;
    this.resetSetUpFormErrors();
    const account: any = this.registerForm.getRawValue();
    account.default_locale = this.i18nHelper.locale;
    account.region = find(this.countryList, { id: parseInt(account.country, 10) }).region.toLowerCase();

    this.register.registerAccount(account).subscribe({
      next: (res) => {
        if (res.data.hasOwnProperty('token')) {
          // Need to set region token as well for the authentication to work properly for the given account
          Cookies.set(constants.AUTH_TOKEN, res.data.token, { domain: environment.DOMAIN });
          Cookies.set(constants.TRACKING_ID, this.jwtHelper.decodeToken(res.data.token).user_id, { domain: environment.DOMAIN });
          Cookies.set(constants.API_REGION_TOKEN, this.jwtHelper.decodeToken(res.data.token).region, { domain: environment.DOMAIN });
        }
        this.qs
          .saveQuestionnaire(this.builder.questionnaire.id, this.i18nHelper.locale)
          .subscribe(() => {
            this.emailReqLoading = false;
            location.assign(`${environment.APP_URL}/request?redirect_from_tb=1`);
          },
            (err) => {
              this.emailReqLoading = false;
              this.showGenericError = true;
              if (environment.ddg_enabled) window.DD_LOGS.logger.error(err);
            });
      },
      error: (err) => {
        this.emailReqLoading = false;
        // Assumes api always sends at least one of these two error types and never both
        if ('enterprise_warning' in err.error || 'email' in err.error) {
          const errors = err.error.enterprise_warning || err.error.email;
          errors.forEach((item: any) => this.register.setUpFormErrors[item] = true);
        } else if ('user_details' in err.error) {
          const errors = err.error.user_details.email || err.error.user_details.non_field_errors;
          errors.forEach((item: any) => this.register.setUpFormErrors[item] = true);
        } else {
          this.showGenericError = true;
        }
      }
    }
    );
  }

  hasNonCorpEmailErr() {
    return (
      this.register.setUpFormErrors.ERROR_DOMAIN_INVALID ||
      this.register.setUpFormErrors.ERROR_NO_PLUS_CHAR
    );
  }

  existingAccountErr() {
    return (
      this.register.setUpFormErrors.ERROR_EMAIL_INVALID ||
      this.register.setUpFormErrors.ERROR_EMAIL_EXISTS ||
      this.register.setUpFormErrors.WARNING_DOMAIN_IS_ENTERPRISE
    );
  }

  sendQuestionnaireSuccess = () => {
    this.questionnaireSending = false;
    this.showGenericError = false;
    this.hideRegisterDialog();
    this.router.navigate([constants.APP_ROUTE_PATHS.sentToEmail]);
  };

  sendQuestionnaireError = (res: any) => {
    this.questionnaireSending = false;
    this.showGenericError = true;
  };

  sendQuestionnaireToEmail() {
    this.questionnaireSending = true;
    this.qs
      .sendQuestionnaireToEmail(this.builder.questionnaire.id)
      .subscribe(this.sendQuestionnaireSuccess, this.sendQuestionnaireError);
  }

  private translateCountries() {
    // changes Country.country in place based on Country.translationKey,
    // only makes sense once translation service is ready
    try {
      const translateObservables = [];
      for (let i = 0; i < this.countryList.length; i++)
        translateObservables.push(this.translate.get('countries.' + this.countryList[i]['translationKey']));
      forkJoin(translateObservables).subscribe({
        next: (value: string[]) => {
          for (let i = 0; i < this.countryList.length; i++) {
            this.countryList[i]['country'] = value[i];
          }
        },
        complete: () => {
          // Sort list based on translated name, using language sensitive string comparison
          this.countryList.sort(function (a, b) {
            return new Intl.Collator().compare(a['country'], b['country']);
          });
        }
      });
    } catch (error) {
      console.warn('Error generating i18n strings for the selected locale.');
    }

  }

  private getMostUsedCountries(mostUsedNames: string[] = []) {
    const reducer = (acc: any[], curr: any) => (mostUsedNames.includes(curr.translationKey) ? [...acc, curr] : acc);
    this.mostUsedCountries = this.countryList.reduce(reducer, []);
  }

}
